import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { quizApi, examLibraryApi, batchApi, apiErrorMessage } from '../services/api';
import { useOrg } from '../context/OrgContext';
import { useToast } from '../context/ToastContext';
import useExamHierarchy from '../hooks/useExamHierarchy';
import { batchesForExam, defaultDurationMinutes } from '../utils/batchExam';
import { Button, Card, Field, PageSkeleton, ErrorState, EmptyState, Spinner } from '../components/ui';

export default function ScreenerPage() {
  const org = useOrg();
  const toast = useToast();
  const navigate = useNavigate();
  const [exams, setExams] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [form, setForm] = useState({
    examId: '', name: '', difficulty: 'Mixed', questionCount: 30,
    batchId: '', opensAt: '', closesAt: '', durationMinutes: '', allowedAttempts: 1
  });
  const [selectedTopicIds, setSelectedTopicIds] = useState([]);
  const [scheduling, setScheduling] = useState(false);

  // Shared, cached exam hierarchy (also used by the exam library and question bank).
  const { sections, loading: topicsLoading, error: topicsError } = useExamHierarchy(form.examId);
  const topics = useMemo(
    () => sections.flatMap((section) => (section.topics || []).map((topic) => ({ ...topic, sectionTitle: section.title }))),
    [sections]
  );

  useEffect(() => { setSelectedTopicIds([]); }, [form.examId]);

  // A test must be on the exam its batch prepares for; the server rejects a
  // mismatch, so only compatible batches are offered.
  const eligibleBatches = useMemo(
    () => batchesForExam(batches, form.examId),
    [batches, form.examId]
  );
  const selectedBatchId = eligibleBatches.some((b) => b.id === form.batchId)
    ? form.batchId
    : (eligibleBatches[0]?.id || '');

  useEffect(() => {
    Promise.all([examLibraryApi.enrolled(org.id), batchApi.list(org.id)])
      .then(([examsRes, batchesRes]) => {
        const enrolledExams = examsRes.data || [];
        const orgBatches = batchesRes.data || [];
        setExams(enrolledExams);
        setBatches(orgBatches);
        setForm((f) => ({
          ...f,
          examId: enrolledExams[0]?.examId || '',
          batchId: orgBatches[0]?.id || ''
        }));
      })
      .catch((err) => setError(apiErrorMessage(err, 'Failed to load screener data')))
      .finally(() => setLoading(false));
  }, [org.id]);

  const toggleTopic = (topicId) => {
    setSelectedTopicIds((items) =>
      items.includes(topicId) ? items.filter((x) => x !== topicId) : [...items, topicId]
    );
  };

  const canSchedule = form.examId && form.name.trim() && selectedBatchId && Number(form.questionCount) > 0 && !scheduling;

  const schedule = async () => {
    setScheduling(true);
    try {
      const payload = {
        examId: form.examId,
        name: form.name.trim(),
        batchId: selectedBatchId,
        topicIds: selectedTopicIds,
        difficulty: form.difficulty,
        questionCount: Number(form.questionCount),
        allowedAttempts: Number(form.allowedAttempts),
        ...(form.opensAt && { opensAt: new Date(form.opensAt).toISOString() }),
        ...(form.closesAt && { closesAt: new Date(form.closesAt).toISOString() }),
        ...(form.durationMinutes && { durationMinutes: Number(form.durationMinutes) })
      };
      const res = await quizApi.screenerLaunch(org.id, payload);
      toast.success(`Screener quiz scheduled with ${res.data.questionCount} questions.`);
      navigate('/manage-quiz');
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to schedule screener quiz'));
    } finally {
      setScheduling(false);
    }
  };

  if (loading) return <PageSkeleton />;
  if (error) return <ErrorState message={error} />;
  if (!exams.length) {
    return (
      <EmptyState title="No enrolled exams yet" action={<Button variant="secondary" onClick={() => navigate('/exam-library')}>Open exam library</Button>}>
        The screener composes quizzes from your enrolled exams. Add one first.
      </EmptyState>
    );
  }
  if (!batches.length) {
    return (
      <EmptyState title="No batches yet" action={<Button variant="secondary" onClick={() => navigate('/batches')}>Create a batch</Button>}>
        Create a batch first so the quiz has an audience.
      </EmptyState>
    );
  }

  return (
    <>
      <div className="screener-steps">
        <span className="current">1. Design quiz</span><i /><span>2. Schedule students</span>
      </div>
      <div className="screener-layout">
        <div className="screener-main">
          <Card className="screener-card">
            <div className="section-title">
              <div>
                <p className="eyebrow">QUESTION COMPOSITION</p>
                <h2>Design a targeted quiz</h2>
                <p>The system will randomly select questions from the exam pool using these rules.</p>
              </div>
            </div>
            <div className="form-grid">
              <Field label="Exam">
                <select value={form.examId} onChange={(e) => setForm({ ...form, examId: e.target.value })}>
                  {exams.map((x) => <option key={x.examId} value={x.examId}>{x.title}</option>)}
                </select>
              </Field>
              <Field label="Difficulty">
                <select value={form.difficulty} onChange={(e) => setForm({ ...form, difficulty: e.target.value })}>
                  <option>Foundation</option><option>Mixed</option><option>Advanced</option>
                </select>
              </Field>
              <Field label="Questions to select">
                <input type="number" min="1" max="200" value={form.questionCount}
                  onChange={(e) => setForm({ ...form, questionCount: e.target.value })} />
              </Field>
            </div>
            <div className="topic-toolbar">
              <h3>Topics to include {selectedTopicIds.length ? `(${selectedTopicIds.length} selected)` : '(all topics)'}</h3>
              {topics.length > 0 && (
                <span>
                  <Button variant="text" onClick={() => setSelectedTopicIds(topics.map((t) => t.id))}>Select all</Button>
                  {selectedTopicIds.length > 0 && <Button variant="text" onClick={() => setSelectedTopicIds([])}>Clear</Button>}
                </span>
              )}
            </div>
            {topicsLoading && <div className="page-loading"><Spinner /> Loading topics…</div>}
            {!topicsLoading && topicsError && <p className="danger" style={{ marginBottom: 10 }}>{topicsError}</p>}
            {!topicsLoading && !topicsError && !topics.length && <EmptyState title="No topics found">This exam has no topics yet — the quiz will draw from the whole pool.</EmptyState>}
            <div className="topic-grid">
              {topics.map((topic) => (
                <button
                  key={topic.id}
                  onClick={() => toggleTopic(topic.id)}
                  className={selectedTopicIds.includes(topic.id) ? 'topic selected' : 'topic'}
                >
                  {selectedTopicIds.includes(topic.id) ? '✓ ' : ''}{topic.title || topic.name}
                </button>
              ))}
            </div>
            <p className="helper" style={{ marginTop: 16 }}>
              Leave all topics unselected to draw from the whole exam pool. Custom question banks arrive post-MVP — for now questions come 100% from the Prepgen.ai bank.
            </p>
          </Card>
        </div>
        <aside className="schedule-panel">
          <Card>
            <p className="eyebrow">SCHEDULE</p>
            <h2>Assign to students</h2>
            <div className="form">
              <Field label="Quiz name">
                <input placeholder="e.g. Algebra screener" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </Field>
              <Field label="Batch">
                <select value={selectedBatchId} onChange={(e) => setForm({ ...form, batchId: e.target.value })}>
                  {eligibleBatches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </Field>
              {!eligibleBatches.length && (
                <p className="helper">
                  No batch is preparing for this exam. Point one at it from the Batches page.
                </p>
              )}
              <Field label="Time limit in minutes (optional)">
                <input type="number" min="1" max="600"
                  placeholder={`Defaults to ${defaultDurationMinutes(form.questionCount)}`}
                  value={form.durationMinutes}
                  onChange={(e) => setForm({ ...form, durationMinutes: e.target.value })} />
              </Field>
              <Field label="Opens (optional, your local time)">
                <input type="datetime-local" value={form.opensAt}
                  onChange={(e) => setForm({ ...form, opensAt: e.target.value })} />
              </Field>
              <Field label="Closes (optional, your local time)">
                <input type="datetime-local" value={form.closesAt}
                  onChange={(e) => setForm({ ...form, closesAt: e.target.value })} />
              </Field>
              <Field label="Allowed attempts">
                <select value={form.allowedAttempts} onChange={(e) => setForm({ ...form, allowedAttempts: e.target.value })}>
                  <option value={1}>1</option><option value={2}>2</option><option value={-1}>Unlimited</option>
                </select>
              </Field>
            </div>
            <div className="quiz-summary">
              <span>Quiz summary</span>
              <b>{form.questionCount} randomized questions</b>
              <small>{selectedTopicIds.length || 'All'} topics · {form.difficulty}</small>
              <small>100% Prepgen.ai bank</small>
            </div>
            <Button disabled={!canSchedule} onClick={schedule}>
              {scheduling ? 'Scheduling…' : 'Schedule quiz'}
            </Button>
            <Button variant="text" onClick={() => navigate('/manage-quiz')}>Cancel</Button>
          </Card>
        </aside>
      </div>
    </>
  );
}
